import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { useTranslation } from 'react-i18next';
import { Radio } from '@mui/material';
import { CANCEL_REASON } from '@src/constants/order';
import { COLOR } from '@src/styles/color';
import CustomTypography from '@src/new-components/Typography';
import LimitedInput from '@src/new-components/Input/LimitedInput';
import CommonButton from '@src/new-components/Button';
import actions from '@src/redux/actions';
import { useHistory } from 'react-router-dom';
import apis from '@src/apis';
import ROUTES from '@src/constants/route';

import {
  StyledCancelReasonContent,
  StyledCancelReasonItem,
} from './index.style';

const CancelReasonDialog = ({ open }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { orderId } = useSelector((state) => state.cancelReasonDialog);
  const [selectedReason, setSelectedReason] = useState(null);
  const [note, setNote] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const MAX_LENGTH_NOTE = 200;

  const handleChangeNote = (e) => {
    if (e.target.value.length > MAX_LENGTH_NOTE) {
      setNote(e.target.value.slice(0, MAX_LENGTH_NOTE));
    } else {
      setNote(e.target.value);
    }
  };

  const handleClose = () => {
    dispatch(actions.cancelReasonDialog.hideCancelReasonDialog());
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const noteTrim = selectedReason === 'OTHER' ? note?.trim() : undefined;
      const data = await apis.orders.cancelOrder(
        orderId,
        noteTrim,
        selectedReason,
      );
      if (!data.status) {
        dispatch(actions.noti.push({ severity: 'error', code: data.code }));
      } else {
        dispatch(actions.user.getLatestOrderSuccess(data.result));
      }
      handleClose();
      history.push(ROUTES.PAYMENT);
    } catch (error) {
      dispatch(
        actions.noti.push({ severity: 'error', message: 'errorOccurred' }),
      );
    } finally {
      setLoading(false);
    }
  };

  const disabledConfirm =
    !selectedReason || (selectedReason === 'OTHER' && !note) || loading;

  const renderContent = () => (
    <StyledCancelReasonContent>
      <CustomTypography
        variant="body-16-medium"
        color={COLOR.darkGray}
        component="p"
      >
        {t('pleaseTellUsTheReason')}
      </CustomTypography>
      {CANCEL_REASON.map((item) => (
        <StyledCancelReasonItem
          key={item.key}
          active={selectedReason === item.key}
          onClick={() => {
            if (loading) return;
            setSelectedReason(item.key);
          }}
        >
          <Radio
            checked={selectedReason === item.key}
            sx={{
              color: COLOR.indigo.default,
              ...(!loading && {
                '&.Mui-checked': {
                  color: COLOR.indigo.default,
                },
              }),
              paddingLeft: '0px',
            }}
            disabled
          />
          <CustomTypography variant="body-14-regular" color={COLOR.black}>
            {t(item.label)}
          </CustomTypography>
        </StyledCancelReasonItem>
      ))}
      {selectedReason === 'OTHER' && (
        <LimitedInput
          name="note"
          value={note}
          placeholder={t('descriptionCancelOrder')}
          onChange={handleChangeNote}
          rows={3}
          maxLength={MAX_LENGTH_NOTE}
          multiline
          customStyle={{
            backgroundColor: COLOR.transparent,
          }}
          disabled={loading}
        />
      )}
    </StyledCancelReasonContent>
  );

  const renderAction = () => (
    <>
      <CommonButton
        variant="contained"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.indigo.default,
        }}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
        }}
        onClick={handleConfirm}
        disabled={disabledConfirm}
      >
        {t('confirm')}
      </CommonButton>
    </>
  );

  return (
    <CommonDialog
      open={open}
      width="512px"
      onClose={handleClose}
      title={t('reasonForCancellation')}
      contentComponent={renderContent()}
      actionComponent={renderAction()}
      disableClose={loading}
      disableAutoClose={loading}
    />
  );
};

export default CancelReasonDialog;
